import React, { useState, useMemo } from 'react';
import users from './data/users.json';
import demoProfiles from './data/demoProfiles.json';
import './App.css';

// Merge user credentials with demo profile details
const enrichedUsers = users.map(u => {
  const profile = demoProfiles.find(p => p.username === u.username) || {};
  return {
    ...profile,
    ...u,
  };
});

export default function Login({ onLogin }) {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [visible, setVisible] = useState(false);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [selectedRole, setSelectedRole] = useState('ALL');

  function submit(event) {
    event.preventDefault();
    const user = users.find(u => u.username === username.trim() && u.password === password);
    if (!user) {
      setError('Username atau password salah. Coba kembali dengan akun contoh di daftar.');
      return;
    }
    try {
      localStorage.setItem('username', user.username);
      localStorage.setItem('password', user.password);
      localStorage.setItem('pasword_keycloak', user.pasword_keycloak);
    } catch {
      ['username', 'password', 'pasword_keycloak'].forEach(key => {
        try { localStorage.removeItem(key); } catch { /* Storage may be unavailable. */ }
      });
      setError('Login belum berhasil disimpan. Izinkan penyimpanan browser lalu coba lagi.');
      return;
    }
    onLogin(user);
  }

  function handleSelectUser(u) {
    setUsername(u.username);
    setPassword(u.password);
    setError('');
  }

  const roleOptions = useMemo(() => {
    const roles = new Set();
    enrichedUsers.forEach(u => {
      if (u.jobRole) roles.add(u.jobRole);
    });
    return Array.from(roles);
  }, []);

  const filteredUsers = useMemo(() => {
    return enrichedUsers.filter(u => {
      const matchesSearch =
        !search ||
        (u.name && u.name.toLowerCase().includes(search.toLowerCase())) ||
        (u.username && u.username.toLowerCase().includes(search.toLowerCase())) ||
        (u.roles && u.roles.toLowerCase().includes(search.toLowerCase())) ||
        (u.email && u.email.toLowerCase().includes(search.toLowerCase()));

      const matchesRole =
        selectedRole === 'ALL' || u.jobRole === selectedRole;

      return matchesSearch && matchesRole;
    });
  }, [search, selectedRole]);

  return (
    <main className="login-page">
      <div className="login-container">
        {/* Left Column: Demo Users List */}
        <section className="login-user-list-card" aria-label="Daftar Contoh User">
          <header className="user-list-header">
            <div className="user-list-title-row">
              <h2 className="user-list-title">Pilih Akun Demo</h2>
              <span className="user-count-badge">{filteredUsers.length} akun</span>
            </div>
            <p className="user-list-subtitle">
              Klik salah satu akun untuk otomatis mengisi form login.
            </p>
            <div className="user-filter-bar">
              <input
                type="text"
                className="user-search-input"
                placeholder="Cari nama, username, atau role..."
                value={search}
                onChange={e => setSearch(e.target.value)}
                aria-label="Cari user demo"
              />
              {roleOptions.length > 0 && (
                <div className="role-pills">
                  <button
                    type="button"
                    className={`role-pill ${selectedRole === 'ALL' ? 'active' : ''}`}
                    onClick={() => setSelectedRole('ALL')}
                  >
                    Semua
                  </button>
                  {roleOptions.map(role => (
                    <button
                      key={role}
                      type="button"
                      className={`role-pill ${selectedRole === role ? 'active' : ''}`}
                      onClick={() => setSelectedRole(role)}
                    >
                      {role}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </header>

          <div className="user-items-scroll" role="list">
            {filteredUsers.length === 0 ? (
              <div className="user-list-empty">Tidak ada user yang sesuai dengan filter.</div>
            ) : (
              filteredUsers.map(u => {
                const isSelected = username === u.username;
                return (
                  <button
                    key={u.username}
                    type="button"
                    role="listitem"
                    className={`user-card-item ${isSelected ? 'selected' : ''}`}
                    onClick={() => handleSelectUser(u)}
                  >
                    <div className="user-avatar" aria-hidden="true">
                      {u.firstName ? u.firstName.charAt(0) : u.username.charAt(0).toUpperCase()}
                    </div>
                    <div className="user-card-info">
                      <div className="user-card-header-row">
                        <span className="user-card-name">{u.name || u.username}</span>
                        {u.jobRole && (
                          <span className={`user-badge ${u.jobRole.toLowerCase()}`}>
                            {u.jobRole}
                          </span>
                        )}
                      </div>
                      <div className="user-card-meta">
                        <span className="user-card-username">@{u.username}</span>
                        {u.head && u.head !== '-' && (
                          <span className="user-card-head">Head: @{u.head}</span>
                        )}
                      </div>
                      {u.roles && <div className="user-card-role">{u.roles}</div>}
                    </div>
                  </button>
                );
              })
            )}
          </div>

          <footer className="user-list-footer">
            <span className="password-hint">
              Default password: <code>CIMB#2026</code>
            </span>
          </footer>
        </section>

        {/* Right Column: Login Form */}
        <section className="login-content" aria-labelledby="login-title">
          <div className="login-header">
            <h1 id="login-title">LOGIN POC CIMB</h1>
            <p className="login-subtitle">
              Silakan login menggunakan kredensial akun Anda.
            </p>
          </div>

          <form onSubmit={submit}>
            <label htmlFor="username">Username</label>
            <input
              id="username"
              name="username"
              autoComplete="username"
              placeholder="Masukkan username"
              value={username}
              onChange={event => {
                setUsername(event.target.value);
                setError('');
              }}
              required
              aria-invalid={!!error}
              aria-describedby={error ? 'login-error' : undefined}
            />

            <label htmlFor="password">Password</label>
            <div className="password-field">
              <input
                id="password"
                name="password"
                type={visible ? 'text' : 'password'}
                autoComplete="current-password"
                placeholder="Masukkan password"
                value={password}
                onChange={event => {
                  setPassword(event.target.value);
                  setError('');
                }}
                required
                aria-invalid={!!error}
                aria-describedby={error ? 'login-error' : undefined}
              />
              <button
                type="button"
                className="password-toggle"
                onClick={() => setVisible(!visible)}
                aria-label={visible ? 'Sembunyikan password' : 'Tampilkan password'}
                aria-pressed={visible}
              >
                {visible ? 'Sembunyikan' : 'Lihat'}
              </button>
            </div>

            {error && (
              <p id="login-error" className="login-error" role="alert">
                {error}
              </p>
            )}

            <button className="login-submit" type="submit">
              Masuk
            </button>
          </form>

          {/* Quick sample fallback */}
          <aside className="sample-account" aria-label="Akun contoh">
            <h2>Akun contoh pertama</h2>
            <dl>
              <div>
                <dt>Username</dt>
                <dd>{users[0].username}</dd>
              </div>
              <div>
                <dt>Password</dt>
                <dd>{users[0].password}</dd>
              </div>
            </dl>
            <button
              type="button"
              className="sample-fill"
              onClick={() => handleSelectUser(users[0])}
            >
              Gunakan akun contoh
            </button>
          </aside>
        </section>
      </div>
    </main>
  );
}
